import { type ComponentProps, useRef, useState } from "react";

import {
    BdsFloat,
    BdsFloatAlign,
    BdsIconInfoCircle,
    BdsRollupButton,
} from "@buildertrend/components";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { Mark, PinTypes } from "commonComponents/entity/map/Map.types";
import { MapButton } from "commonComponents/entity/map/MapButton/MapButton";

import "./AuditInfoButton.less";

import UserInfoViewDisplay, { type IUserInfoViewDisplayProps } from "./UserInfoViewDisplay";

interface IAuditInfoButtonProps {
    id: string;
    displayType: ComponentProps<typeof BdsRollupButton>["displayType"];
    minStage?: number;
    userDetails: IUserInfoViewDisplayProps;
    latitude?: number;
    longitude?: number;
    isLocationTrackingEnabled?: boolean;
    useRollupButton?: boolean;
    floatAlign?: BdsFloatAlign;
}

export const AuditInfoButton = ({
    id,
    displayType,
    minStage,
    userDetails,
    latitude,
    longitude,
    isLocationTrackingEnabled,
    useRollupButton,
    floatAlign = BdsFloatAlign.BR,
}: IAuditInfoButtonProps) => {
    const infoIconRef = useRef<HTMLElement>(null);
    const [infoPopover, setInfoPopover] = useState(false);
    const [isMapOpen, setIsMapOpen] = useState(false);
    const setInfoPopoverWithMap = (value: boolean) => {
        if (!isMapOpen) {
            setInfoPopover(value);
        }
    };

    const onInfoClick = () => {
        setInfoPopover(!infoPopover);
    };

    const showLocation =
        latitude !== undefined && longitude !== undefined && isLocationTrackingEnabled;

    let mark;
    if (showLocation) {
        mark = new Mark({
            id: "dailyLogMark",
            position: { lat: latitude, lng: longitude },
            pinType: PinTypes.Default,
            animate: false,
        });
    }

    return (
        <>
            {useRollupButton && minStage ? (
                <span ref={infoIconRef} className="info-button-span">
                    <BdsRollupButton
                        id={id}
                        testid={id}
                        value="info"
                        ariaLabel="Publish info"
                        icon={<BdsIconInfoCircle />}
                        minStage={minStage}
                        onClick={onInfoClick}
                        enableTooltipClick
                        tooltip="Publish info"
                        displayType={displayType}
                    />
                </span>
            ) : (
                <span ref={infoIconRef} className="info-button-span">
                    <BTPopover content="Publish info">
                        <BTButton
                            id={id}
                            data-testid={id}
                            icon={<BdsIconInfoCircle />}
                            onClick={onInfoClick}
                            type={displayType}
                            aria-label="Publish info"
                        />
                    </BTPopover>
                </span>
            )}

            <BdsFloat
                source={infoIconRef}
                open={infoPopover}
                align={floatAlign}
                ariaLabel="publishInfoFloat"
                testid="publishInfoFloat"
                className="publishInfoFloat"
                role="tooltip"
                setOpen={setInfoPopoverWithMap}
            >
                <div className="flex flex-row gap-2">
                    <UserInfoViewDisplay {...userDetails} />
                    {showLocation ? (
                        <MapButton
                            data-testid="openInMap"
                            type="link"
                            className="mapButton"
                            modalTitle="Daily Log Map"
                            mark={mark}
                            readOnly
                            onClick={() => {
                                setIsMapOpen(true);
                            }}
                            onClose={() => {
                                setIsMapOpen(false);
                            }}
                        />
                    ) : (
                        <></>
                    )}
                </div>
            </BdsFloat>
        </>
    );
};
